<template lang="html">
<div class="flow-duration-tool">
    <div v-if="!queryDone" class="no-data grey lighten-2 grey--text">
        <span>Loading...</span>
    </div>
    <div v-if="queryDone && !hasData" class="no-data grey lighten-2 grey--text">
        <span>No data available</span>
    </div>
    <div v-if="hasData" class="chart-wrap">
        <div class="date-select-wrap">
            <filter-date-picker
            :monthsFilter="monthsFilter"
            :yearsFilter="yearsFilter"
            :dateRange="dateRange"
            @reset="resetFilters"
            @setMonths="applyMonthFilter"
            @setYears="applyYearFilter"
            />
        </div>
        <div class="charts-layout-wrap">
            <div class="charts-col charts-col-1">
                <div class="fdt-chart monthly-flow-statistics">
                    <h1 class="headline">Monthly Flow Statistics</h1>
                    <monthly-flow
                    v-if="monthDataAll.length"
                    @updateFilters="applyMonthFilter"
                    :dimensionFilter="monthsFilter"
                    :data="monthData"
                    :dataAll="monthDataAll" />
                </div>
                <div class="fdt-chart flow-duration-curve">
                    <h1 class="headline">Flow Duration</h1>
                    <flow-duration-curve
                    v-if="curveDataAll.length"
                    :data="curveData"
                    />
                </div>
            </div>
            <div class="charts-col charts-col-2">
                <div class="fdt-chart annual-total-runoff">
                    <h1 class="headline">Total Runoff</h1>
                    <annual-runoff
                    v-if="yearDataAll.length"
                    @updateFilters="applyYearFilter"
                    :dimensionFilter="yearsFilter"
                    :data="yearData"
                    :dataAll="yearDataAll" />
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import crossfilter from 'crossfilter2';
import reductio from 'reductio';
import { apolloClient } from '@/apollo/apollo';
import gql from 'graphql-tag';
import * as d3 from 'd3';

import * as actions from '@/constants/actions';
import MonthlyFlow from '@/components/charts/FDMonthlyFlowStats';
import AnnualRunoff from '@/components/charts/FDAnnualRunoff';
import FlowDurationCurve from '@/components/charts/FlowDurationCurve';
import FilterDatePicker from '@/components/charts/FDDatePicker';

export default {
    components: {
        MonthlyFlow,
        AnnualRunoff,
        FlowDurationCurve,
        FilterDatePicker,
    },
    data() {
        return {
            queryDone: false,
            // data from the API
            flowDurationData: null,
            // crossfilter object
            cf: null,
            // dimensions
            valuesDimension: null,
            monthsDimension: null,
            monthsFilter: null,
            yearsDimension: null,
            yearsFilter: null,
            // groups
            monthsGroup: null,
            yearsByTotalGroup: null,
            // chart data
            yearData: [],
            yearDataAll: [],
            monthData: [],
            monthDataAll: [],
            curveData: [],
            curveDataAll: [],
        };
    },
    computed: {
        station() {
            return this.$store.state.map.selectedStation;
        },
        hasData() {
            const data = this.flowDurationData;
            return this.queryDone && data && data.length > 0;
        },
        // min and max years for the date picker component
        dateRange() {
            const min = d3.min(this.yearDataAll, d => d.key);
            const max = d3.max(this.yearDataAll, d => d.key);
            return {
                min,
                max,
            };
        },
    },
    // re-initialize on re-mount
    mounted() {
        this.initialize();
    },
    methods: {
        /**
         * get flow duration data from the API
         */
        async getData() {
            this.queryDone = false;

            if (!this.station) return;

            this.$store.dispatch(actions.ADD_LOADING, 'load flow duration data');
            try {
                const { data } = await apolloClient.query({
                    query: gql`query stationStreamflow($id: Int!) {
                        getStation(id: $id) {
                            flow_duration_tool
                        }
                    }`,
                    variables: {
                        id: this.station.id,
                        type: 1,
                    },
                });

                // populate variables
                const dataWithDates = data.getStation.flow_duration_tool.map((e) => {
                    const date = new Date(e.d);
                    return {
                        d: date,
                        v: e.v,
                    };
                });
                this.flowDurationData = dataWithDates;
                this.queryDone = true;
            } catch (ex) {
                this.$store.dispatch(actions.TRIGGER_TOAST, 'Error loading flow duration data');
            } finally {
                this.$store.dispatch(actions.REMOVE_LOADING, 'load flow duration data');
            }
        },
        // initialize crossfilter on new data
        async initialize() {
            await this.getData();

            if (!this.flowDurationData) {
                return;
            }
            // initialize crossfilter and define dimensions
            this.cf = crossfilter(this.flowDurationData);
            // fire a callback when filters change
            this.cf.onChange(this.cfChanged);
            // define dimensions
            this.valuesDimension = this.cf.dimension(d => d.v);
            this.monthsDimension = this.cf.dimension(d => d.d.getMonth() + 1);
            this.yearsDimension = this.cf.dimension(d => d.d.getFullYear());

            // group by annual total
            this.yearsByTotalGroup = this.yearsDimension.group().reduceSum(d => d.v);
            // group by month
            this.monthsGroup = this.monthsDimension.group();

            // monthly min, max, median, 75%, 25%
            const monthReducer = reductio()
            .valueList(d => d.v)
            .min(true)
            .max(true)
            .median(true)
            .count(true);

            // set the month group's reducer to use reductio
            monthReducer(this.monthsGroup);

            // initialize local data
            this.setLocalData();
            // store the initial, unfiltered data
            this.yearDataAll = JSON.parse(JSON.stringify(this.yearsByTotalGroup.all()));
            this.monthDataAll = JSON.parse(JSON.stringify(this.monthsGroup.all()));
            // all dimension values, sorted and not yet filtered
            this.curveDataAll = JSON.parse(JSON.stringify(this.valuesDimension.top(Infinity)));
        },
        /**
         * callback for crossfilter change events
         * @param  {String} evType crossfilter event type
         */
        cfChanged() {
            // update vm as needed with the changed (filtered) datasets
            this.setLocalData();
            // update filter props passed to chart components
            this.monthsFilter = this.monthsDimension.currentFilter() || null;
            this.yearsFilter = this.yearsDimension.currentFilter() || null;

            // @TODO: whatever's needed for the flow duration chart
        },
        /**
         * update local data with current group values
         */
        setLocalData() {
            this.yearData = this.yearsByTotalGroup.all();
            this.monthData = this.monthsGroup.all();
            // all dimension values, sorted and filtered
            this.curveData = this.valuesDimension.top(Infinity);
        },
        /**
         * set filter on years dimension
         * pass null to clear filter
         * @param  {Array|null} years params for dimension.filter method
         */
        applyYearFilter(years) {
            this.yearsDimension.filter(years);
        },
        /**
         * set filter on months dimension
         * pass null to clear filter
         * @param  {Array|null} months params for dimension.filter method
         */
        applyMonthFilter(months) {
            this.monthsDimension.filter(months);
        },
        /**
         * reset all filters to null
         */
        resetFilters() {
            this.applyYearFilter(null);
            this.applyMonthFilter(null);
        },
    },
};
</script>

<style lang="scss">
.flow-duration-tool {
    $tabbarHeight: 128px;
    $toolbarHeight: 5rem;
    $yPadding: 2rem;
    $xPadding: 16px;
    $contentWidth: calc(100vw - (#{$xPadding} * 2));
    $contentHeight: calc(100vh - #{$tabbarHeight} - #{$toolbarHeight} - #{$yPadding});

    // for absolute positioning tooltips
    position: relative;
    overflow: hidden;
    height: calc(100vh - #{$tabbarHeight} - #{$yPadding});

    .date-select-wrap {
        display: inherit;
        justify-content: flex-end;
        height: $toolbarHeight;
    }

    .charts-layout-wrap {
        display: flex;
        align-items: stretch;
        height: $contentHeight;
    }

    .charts-col {
        display: flex;
        flex-direction: column;
        &.charts-col-1 {
            width: calc(#{$contentWidth} * 2/3);
        }
        &.charts-col-2 {
            width: calc(#{$contentWidth} * 1/3);
        }

        .fdt-chart {
            flex: 1 1;
            display: flex;
            flex-direction: column;
        }

        // scroll annual runoff chart when there are many years
        .annual-total-runoff {
            overflow-y: scroll;
        }
    }

    .no-data {
        height: 15rem;
        display: flex;
        justify-content: center;
        align-items: center;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/charts/FlowDurationTool.vue